import React from 'react';
import { Box, Card, Typography, Chip, Divider } from '@mui/material';
import { CheckCircle as CheckIcon, Cancel as CancelIcon } from '@mui/icons-material';

/** Estructura de la ficha detallada de MoCA (replica el formulario impreso, por dominio). */
export interface MocaDetailedData {
  trail: boolean; cube: boolean; clockContour: boolean; clockNumbers: boolean; clockHands: boolean;
  lion: boolean; rhino: boolean; camel: boolean;
  memoriaAttempt1: boolean[]; memoriaAttempt2: boolean[];
  digitsForward: boolean; digitsBackward: boolean;
  letterTap: boolean;
  serial7: boolean[];
  sentence1: boolean; sentence2: boolean;
  fluencyCount: number | undefined;
  abstraction1: boolean; abstraction2: boolean;
  recall: boolean[];
  recallCategoryCue: boolean[];
  recallMultipleChoiceCue: boolean[];
  orientationDay: boolean; orientationMonth: boolean; orientationYear: boolean;
  orientationWeekday: boolean; orientationPlace: boolean; orientationCity: boolean;
}

export const MOCA_WORDS = ['Rojo', 'Seda', 'Iglesia', 'Clavel', 'Cara'];
export const MOCA_SERIAL7_VALUES = [93, 86, 79, 72, 65];

export const calcMocaSerial7Points = (correctCount: number): number => {
  if (correctCount >= 4) return 3;
  if (correctCount >= 2) return 2;
  if (correctCount >= 1) return 1;
  return 0;
};

const Mark: React.FC<{ checked: boolean }> = ({ checked }) =>
  checked
    ? <CheckIcon fontSize="small" sx={{ color: 'success.main', flexShrink: 0 }} />
    : <CancelIcon fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }} />;

const ItemRow: React.FC<{ label: string; checked: boolean }> = ({ label, checked }) => (
  <Box display="flex" alignItems="center" gap={0.75} sx={{ py: 0.25 }}>
    <Mark checked={checked} />
    <Typography variant="body2" sx={{ color: checked ? 'text.primary' : 'text.secondary' }}>
      {label}
    </Typography>
  </Box>
);

const DomainCard: React.FC<{ title: string; score: number; max: number; children: React.ReactNode }> = ({ title, score, max, children }) => (
  <Card variant="outlined" sx={{ p: 2 }}>
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
      <Typography variant="subtitle2" fontWeight="bold" color="primary">{title}</Typography>
      {max > 0 && (
        <Chip
          size="small"
          label={`${score} / ${max}`}
          color={score === max ? 'success' : score === 0 ? 'default' : 'warning'}
          variant={score === max ? 'filled' : 'outlined'}
        />
      )}
    </Box>
    {children}
  </Card>
);

interface MocaDetailedViewProps {
  /** Datos de la ficha detallada. `undefined`/`null` = prueba antigua guardada solo con puntaje directo (sin detalle por ítem). */
  data?: Partial<MocaDetailedData> | null;
}

const MocaDetailedView: React.FC<MocaDetailedViewProps> = ({ data }) => {
  if (!data) {
    return (
      <Card variant="outlined" sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
        <Typography variant="body2" color="text.secondary">
          Esta prueba fue registrada solo con el puntaje directo total (versión anterior de MoCA). No hay detalle de ítems marcados/no marcados disponible.
        </Typography>
      </Card>
    );
  }

  const d = data;
  const b = (v: boolean | undefined) => !!v;
  const arr = (v: boolean[] | undefined, len: number) => (Array.isArray(v) && v.length === len ? v : Array(len).fill(false));

  const visuoScore = [d.trail, d.cube, d.clockContour, d.clockNumbers, d.clockHands].filter(Boolean).length;
  const identScore = [d.lion, d.rhino, d.camel].filter(Boolean).length;
  const serial7Arr = arr(d.serial7, 5);
  const serial7Correct = serial7Arr.filter(Boolean).length;
  const serial7Points = calcMocaSerial7Points(serial7Correct);
  const attentionScore = (b(d.digitsForward) ? 1 : 0) + (b(d.digitsBackward) ? 1 : 0) + (b(d.letterTap) ? 1 : 0) + serial7Points;
  const fluencyPoint = (d.fluencyCount !== undefined && d.fluencyCount >= 11) ? 1 : 0;
  const languageScore = (b(d.sentence1) ? 1 : 0) + (b(d.sentence2) ? 1 : 0) + fluencyPoint;
  const abstractionScore = (b(d.abstraction1) ? 1 : 0) + (b(d.abstraction2) ? 1 : 0);
  const recallArr = arr(d.recall, 5);
  const recallScore = recallArr.filter(Boolean).length;
  const orientationScore = [d.orientationDay, d.orientationMonth, d.orientationYear, d.orientationWeekday, d.orientationPlace, d.orientationCity].filter(Boolean).length;

  const memoria1 = arr(d.memoriaAttempt1, 5);
  const memoria2 = arr(d.memoriaAttempt2, 5);
  const categoryCue = arr(d.recallCategoryCue, 5);
  const mcCue = arr(d.recallMultipleChoiceCue, 5);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <DomainCard title="Visuoespacial / Ejecutiva" score={visuoScore} max={5}>
        <ItemRow label="Trail (alternado 1-A-2-B...)" checked={b(d.trail)} />
        <ItemRow label="Copia del cubo" checked={b(d.cube)} />
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="caption" color="text.secondary">Reloj (Once y diez):</Typography>
        <ItemRow label="Contorno" checked={b(d.clockContour)} />
        <ItemRow label="Números" checked={b(d.clockNumbers)} />
        <ItemRow label="Agujas" checked={b(d.clockHands)} />
      </DomainCard>

      <DomainCard title="Identificación" score={identScore} max={3}>
        <ItemRow label="León" checked={b(d.lion)} />
        <ItemRow label="Rinoceronte" checked={b(d.rhino)} />
        <ItemRow label="Camello" checked={b(d.camel)} />
      </DomainCard>

      <DomainCard title="Memoria (registro — sin puntaje directo)" score={0} max={0}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {[{ label: '1er intento', values: memoria1 }, { label: '2º intento', values: memoria2 }].map(({ label, values }) => (
            <Box key={label} display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <Typography variant="caption" sx={{ width: 90, flexShrink: 0 }}>{label}</Typography>
              {MOCA_WORDS.map((word, i) => (
                <Box key={word} display="flex" alignItems="center" gap={0.5}>
                  <Mark checked={values[i]} />
                  <Typography variant="caption">{word}</Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </DomainCard>

      <DomainCard title="Atención" score={attentionScore} max={6}>
        <ItemRow label="Dígitos directo (2-1-8-5-4)" checked={b(d.digitsForward)} />
        <ItemRow label="Dígitos inverso (7-4-2)" checked={b(d.digitsBackward)} />
        <Box sx={{ ml: 3.5, mb: 0.5 }}>
          <Chip size="small" label={`${(b(d.digitsForward) ? 1 : 0) + (b(d.digitsBackward) ? 1 : 0)}/2`} color="info" variant="outlined" />
        </Box>
        <ItemRow label="Golpecito con letra A (≤2 errores)" checked={b(d.letterTap)} />
        <Box sx={{ ml: 3.5, mb: 0.5 }}>
          <Chip size="small" label={`${b(d.letterTap) ? 1 : 0}/1`} color="info" variant="outlined" />
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="caption" color="text.secondary">
          {`Resta de 7 en 7 (${serial7Correct}/5 correctas → ${serial7Points} pts):`}
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1.5} mt={0.5} alignItems="center">
          {MOCA_SERIAL7_VALUES.map((val, i) => (
            <Box key={val} display="flex" alignItems="center" gap={0.5}>
              <Mark checked={serial7Arr[i]} />
              <Typography variant="caption">{val}</Typography>
            </Box>
          ))}
          <Chip size="small" label={`${serial7Points}/3`} color="info" variant="outlined" />
        </Box>
      </DomainCard>

      <DomainCard title="Lenguaje" score={languageScore} max={3}>
        <ItemRow label='Repetición: "El gato se esconde bajo el sofá cuando los perros entran en la sala"' checked={b(d.sentence1)} />
        <ItemRow label='Repetición: "Espero que él le entregue el mensaje una vez que ella se lo pida"' checked={b(d.sentence2)} />
        <Box sx={{ ml: 3.5, mb: 0.5 }}>
          <Chip size="small" label={`${(b(d.sentence1) ? 1 : 0) + (b(d.sentence2) ? 1 : 0)}/2`} color="info" variant="outlined" />
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Box display="flex" alignItems="center" gap={0.75}>
          <Mark checked={fluencyPoint === 1} />
          <Typography variant="body2" sx={{ color: fluencyPoint === 1 ? 'text.primary' : 'text.secondary' }}>
            {`Fluidez con "P": ${d.fluencyCount !== undefined ? `${d.fluencyCount} palabras` : 'sin registrar'} (≥11 = 1 pto)`}
          </Typography>
          <Chip size="small" label={`${fluencyPoint}/1`} color="info" variant="outlined" />
        </Box>
      </DomainCard>

      <DomainCard title="Abstracción" score={abstractionScore} max={2}>
        <ItemRow label="Tren - Bicicleta" checked={b(d.abstraction1)} />
        <ItemRow label="Reloj - Regla" checked={b(d.abstraction2)} />
      </DomainCard>

      <DomainCard title="Recuerdo Diferido" score={recallScore} max={5}>
        <Box display="flex" flexWrap="wrap" gap={1.5}>
          {MOCA_WORDS.map((word, i) => (
            <Box key={word} display="flex" alignItems="center" gap={0.5}>
              <Mark checked={recallArr[i]} />
              <Typography variant="body2">{word}</Typography>
            </Box>
          ))}
        </Box>
        {(categoryCue.some(Boolean) || mcCue.some(Boolean)) && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Pistas usadas (no suman puntos):
            </Typography>
            {[{ label: 'Categoría', values: categoryCue }, { label: 'Elección múltiple', values: mcCue }].map(({ label, values }) => (
              values.some(Boolean) ? (
                <Box key={label} display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={0.5}>
                  <Typography variant="caption" sx={{ width: 120, flexShrink: 0 }}>{label}:</Typography>
                  {MOCA_WORDS.map((word, i) => values[i] && (
                    <Chip key={word} size="small" label={word} variant="outlined" />
                  ))}
                </Box>
              ) : null
            ))}
          </>
        )}
      </DomainCard>

      <DomainCard title="Orientación" score={orientationScore} max={6}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <ItemRow label="Día del mes" checked={b(d.orientationDay)} />
          <ItemRow label="Mes" checked={b(d.orientationMonth)} />
          <ItemRow label="Año" checked={b(d.orientationYear)} />
          <ItemRow label="Día de la semana" checked={b(d.orientationWeekday)} />
          <ItemRow label="Lugar" checked={b(d.orientationPlace)} />
          <ItemRow label="Comuna" checked={b(d.orientationCity)} />
        </Box>
      </DomainCard>
    </Box>
  );
};

export default MocaDetailedView;
